import type {useSortable} from '@github-ui/drag-and-drop'
import {useCachePreheatIssueViewer} from '@github-ui/issue-viewer/UseCachePreheatIssueViewer'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {type ComponentProps, createContext, memo, useMemo} from 'react'

import {getVisibleCells} from '../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableSelectedRowIds} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {getHovercardSubjectTag, IssueModel} from '../../models/memex-item-model'
import {useReorderableRow} from './row-reordering/reorderable-rows'
import {TableCell} from './table-cell'
import type {TableDataType} from './table-data-type'
import styles from './table-row.module.css'
import {rowTestId, subRowTestId} from './test-identifiers'
import type {ItemDataType} from './use-item-data'
import {getSelectionInfo, isRowActionable} from './use-row-selection'

export type TableRowProps = {
  row: Row<TableDataType>
  index: number
  data: ItemDataType
}

type ItemIdentifier = ReturnType<typeof IssueModel.prototype.getItemIdentifier>

export const StyledTableRow = ({
  ref,
  itemIdentifier,
  className,
  ...props
}: ComponentProps<'div'> & {
  ref?: React.Ref<HTMLElement | null>
  itemIdentifier?: ItemIdentifier
}) => {
  const component = <div className={clsx(className, styles.StyledTableRow)} {...props} ref={ref} />
  if (itemIdentifier) {
    return (
      <StyledTableRowForIssueWithPreheating itemIdentifier={itemIdentifier}>
        {component}
      </StyledTableRowForIssueWithPreheating>
    )
  }
  return component
}
StyledTableRow.displayName = 'StyledTableRow'

const StyledTableRowForIssueWithPreheating = ({
  itemIdentifier,
  children,
}: {
  itemIdentifier: NonNullable<ItemIdentifier>
  children: React.ReactNode
}) => {
  useCachePreheatIssueViewer({
    owner: itemIdentifier.owner,
    repo: itemIdentifier.repo,
    number: itemIdentifier.number,
    source: 'memex-row',
    featureFlag: 'issues_preheating_memex',
  })

  return <>{children}</>
}

interface RowContext {
  rowNumber: number
  setSortActivatorNodeRef?: (node: HTMLElement | null) => void
  sortListeners?: ReturnType<typeof useSortable>['listeners']
  itemData?: ItemDataType
}

export const RowContext = createContext<RowContext>({
  rowNumber: 0,
  itemData: {focusCellElement: () => {}, rowIdWithOmnibar: null, setRowIdWithOmnibar: () => {}},
})

export const TableRow = memo((props: TableRowProps) => {
  const itemIdentifier = props.row.original.contentType === 'Issue' ? props.row.original.getItemIdentifier() : undefined
  const table = useTableInstance()
  const flatRows = table.getRowModel().flatRows
  const selectedRowIds = useTableSelectedRowIds()

  const {setNodeRef, setActivatorNodeRef, listeners, isSorting, isDragging, isDropped} = useReorderableRow(props.row)

  // Because the row reference provided to TableRow does not change when a row is selected, bringing in the stateful
  // `useTableSelectedRowIds` from table state ensures that rows and their children render with the correct selection
  // state.
  const isSelected = Boolean(selectedRowIds?.[props.row.id]) && isRowActionable(props.row)
  const {beforeSelected, afterSelected, firstInGroup, lastInGroup} = getSelectionInfo(props.row, flatRows)
  const className = clsx({
    hoverable: !isSorting,
    'row-selected': isSelected,
    'row-highlight': isDragging || isDropped,
    'selection-top-edge': isSelected && (!beforeSelected || firstInGroup),
    'selection-bottom-edge': isSelected && (!afterSelected || lastInGroup),
  })

  const context = useMemo(
    () => ({
      rowNumber: props.index + 1,
      setSortActivatorNodeRef: setActivatorNodeRef,
      sortListeners: listeners,
      itemData: props.data,
    }),
    [props.index, setActivatorNodeRef, listeners, props.data],
  )
  return (
    <RowContext value={context}>
      <StyledTableRow
        role="row"
        className={className}
        {...testIdProps(
          props.row.subIssueData?.subIssueDepth !== undefined && props.row.subIssueData.subIssueDepth > 0
            ? subRowTestId(
                props.row
                  .getParentRows()
                  // get last N rows where N is the subIssueDepth
                  .slice(-props.row.subIssueData.subIssueDepth)
                  .map(r => r.index)
                  .concat(props.row.index),
              )
            : rowTestId(props.row.index),
        )}
        data-test-row-is-selected={isSelected}
        data-hovercard-subject-tag={getHovercardSubjectTag(props.row.original)}
        ref={setNodeRef}
        itemIdentifier={itemIdentifier}
      >
        {getVisibleCells(props.row).map(cell => {
          return (
            <TableCell
              row={cell.row}
              column={cell.column}
              key={cell.column.id}
              focusCellElement={props.data.focusCellElement}
              isDragging={isSorting}
            />
          )
        })}
      </StyledTableRow>
    </RowContext>
  )
})

TableRow.displayName = 'TableRow'

try{ StyledTableRowForIssueWithPreheating.displayName ||= 'StyledTableRowForIssueWithPreheating' } catch {}
try{ RowContext.displayName ||= 'RowContext' } catch {}